import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { appColor, appMonogram, isLightColor } from '@/lib/apps'

type AppIconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface AppIconProps {
    name: string
    size?: AppIconSize
    shape?: 'rounded' | 'circle'
    label?: boolean
    className?: string
}

const sizeClasses: Record<AppIconSize, string> = {
    xs: 'size-6 text-[9.5px]',
    sm: 'size-8 text-[11.5px]',
    md: 'size-10 text-[13.5px]',
    lg: 'size-12 text-[15px]',
    xl: 'size-14 text-[17px]'
}

const radiusClasses: Record<AppIconSize, string> = {
    xs: 'rounded-[7px]',
    sm: 'rounded-[9px]',
    md: 'rounded-[11px]',
    lg: 'rounded-[13px]',
    xl: 'rounded-[15px]'
}

// Typographic app tile: the app's brand color with a monogram, used wherever an app logo appears.
export const AppIcon: FC<AppIconProps> = ({ name, size = 'md', shape = 'rounded', label = false, className }): ReactNode => {
    const color = appColor(name)
    const monogram = appMonogram(name)
    const light = isLightColor(color)

    return (
        <span
            role={label ? 'img' : undefined}
            aria-label={label ? name : undefined}
            aria-hidden={label ? undefined : true}
            className={cn(
                'relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden font-semibold leading-none tracking-[-0.04em] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12),0_1px_2px_rgba(14,14,14,0.12)]',
                sizeClasses[size],
                shape === 'circle' ? 'rounded-full' : radiusClasses[size],
                className
            )}
            style={{ backgroundColor: color, color: light ? '#0e0e0e' : '#ffffff' }}
        >
            <span aria-hidden='true' className='absolute inset-0 bg-gradient-to-b from-white/25 via-white/0 to-black/10' />

            <span className='relative' style={monogram.length > 2 ? { fontSize: '0.74em' } : undefined}>
                {monogram}
            </span>
        </span>
    )
}
